// Only documented dashboard-filter selectedValues are persisted. Never serialize
// an .rdash, replace a dashboard, or invoke the embed's save callback.
export const FILTER_STATE_VERSION = 1;

const clone = value => JSON.parse(JSON.stringify(value));
const isRecord = value => !!value && typeof value === "object" && !Array.isArray(value);
const key = value => JSON.stringify(value, (_, v) => {
  if (isRecord(v)) return Object.fromEntries(Object.keys(v).sort().map(k => [k, v[k]]));
  return v;
});
// A user's pick reads back as the bare field value (4); getFilterValues() reports {field: 4}.
const fieldValue = value => {
  if (!isRecord(value)) return value;
  const fields = Object.values(value);
  return fields.length === 1 ? fields[0] : value;
};
const matches = (candidate, value) =>
  key(candidate) === key(value) || key(fieldValue(candidate)) === key(fieldValue(value));
const fallbackLabel = value => (isRecord(value) ? Object.values(value).join(", ") : String(value));
const labelFor = (match, value) =>
  (match && match.label !== null && match.label !== undefined ? String(match.label) : fallbackLabel(value));

export class StaleFilterError extends Error {
  constructor(filterTitle, missingLabels) {
    super("Saved filter values are unavailable.");
    this.name = "StaleFilterError";
    this.filterTitle = filterTitle;
    this.missingLabels = missingLabels;
  }
}

export class UnsavedDashboardError extends Error {
  constructor(change) {
    super("The dashboard has unsaved filter changes.");
    this.name = "UnsavedDashboardError";
    this.change = change;
  }
}

export const filterIds = dashboard => Array.from(dashboard.filters).map(filter => filter.id);

export function unsavedFilterChange(dashboard, savedIds) {
  const saved = new Set(savedIds);
  const added = Array.from(dashboard.filters).find(filter => !saved.has(filter.id));
  if (added) return added.title ? `the "${added.title}" filter isn't saved yet` : "a new filter isn't saved yet";
  const live = new Set(filterIds(dashboard));
  return savedIds.some(id => !live.has(id)) ? "a removed filter is still in the saved dashboard" : null;
}

const [ALL, LAST, PREVIOUS, TO_DATE, THIS, NEXT] = [0, 1, 2, 3, 4, 5];
const [DAY, MONTH, QUARTER, YEAR] = [0, 2, 3, 5];
const PERIODS = ["day", "week", "month", "quarter", "semester", "year"];
const PRESETS = {
  today: [THIS, 0, DAY, "Today"], yesterday: [PREVIOUS, 0, DAY, "Yesterday"],
  lastWeek: [LAST, 7, DAY], lastMonth: [LAST, 30, DAY], lastYear: [LAST, 365, DAY], trailingTwelveMonths: [LAST, 12, MONTH],
  monthToDate: [TO_DATE, 0, MONTH], quarterToDate: [TO_DATE, 0, QUARTER], yearToDate: [TO_DATE, 0, YEAR],
  thisMonth: [THIS, 0, MONTH], thisQuarter: [THIS, 0, QUARTER], thisYear: [THIS, 0, YEAR],
  previousMonth: [PREVIOUS, 0, MONTH], previousQuarter: [PREVIOUS, 0, QUARTER], previousYear: [PREVIOUS, 0, YEAR],
  nextMonth: [NEXT, 0, MONTH], nextQuarter: [NEXT, 0, QUARTER], nextYear: [NEXT, 0, YEAR]
};

export const isDateFilter = filter => !!filter && typeof filter === "object" && "dateFilterType" in filter;

const isoDate = value => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) throw new Error("This date filter cannot be saved.");
  return date.toISOString();
};

const ruleLabel = ({ relation, count, period }) => {
  const unit = PERIODS[period] ?? "period";
  if (relation === TO_DATE) return `${unit[0].toUpperCase()}${unit.slice(1)} to date`;
  if (relation === LAST && count > 0) return `Last ${count} ${unit}${count === 1 ? "" : "s"}`;
  return `${["", "Last", "Previous", "", "This", "Next"][relation] ?? "Period"} ${unit}`;
};

const ruleSelection = (relation, count, period, label) => {
  if (relation === ALL) return [];
  const value = { date_filter: "rule", relation, count: relation === LAST ? count : 0, period };
  return [{ value, label: label ?? ruleLabel(value) }];
};

function dateSelection(filter) {
  const type = String(filter.dateFilterType ?? "allTime");
  if (type === "customRange") {
    const { from, to } = filter.range ?? {};
    const value = { date_filter: "range", from: isoDate(from), to: isoDate(to) };
    return [{ value, label: `${value.from.slice(0, 10)} to ${value.to.slice(0, 10)}` }];
  }
  if (type === "customRule") {
    const rule = filter.rule;
    return rule ? ruleSelection(rule.periodRelation, rule.count ?? 0, rule.periodType) : [];
  }
  const preset = PRESETS[type];
  return preset ? ruleSelection(...preset) : [];
}

const dateValue = filter => dateSelection(filter)[0]?.value ?? null;

const validDateValue = value => isRecord(value) && (
  (value.date_filter === "range" && [value.from, value.to].every(v => !Number.isNaN(new Date(v).getTime()))) ||
  (value.date_filter === "rule" && [value.relation, value.count, value.period].every(Number.isInteger)));

function setDate(filter, value) {
  if (key(dateValue(filter)) === key(value)) return;
  const sdk = globalThis.Reveal;
  if (!value) filter.rule = sdk.RVDateRule.AllTime;
  else if (value.date_filter === "range") filter.range = new sdk.RVDateRange(new Date(value.from), new Date(value.to));
  else if (value.relation === LAST) filter.rule = new sdk.RVDateRule(value.relation, value.count, value.period);
  else filter.rule = new sdk.RVDateRule(value.relation, value.period);
}

const liveFilter = (dashboard, filter) =>
  (dashboard ? Array.from(dashboard.filters).find(candidate => candidate.id === filter.id) : null) ?? filter;

const currentValues = filter => (isDateFilter(filter) ? dateSelection(filter).map(s => s.value) : filter.selectedValues ?? []);

const hiddenSet = hiddenIds => new Set(Array.isArray(hiddenIds) ? hiddenIds : []);

export async function captureFilterState(dashboard, hiddenIds = []) {
  if (!dashboard) throw new Error("Dashboard is not ready.");
  const hidden = hiddenSet(hiddenIds);
  const visible = Array.from(dashboard.filters).filter(filter => !hidden.has(filter.id));
  const filters = await Promise.all(visible.map(async filter => {
    if (isDateFilter(filter)) {
      return { id: filter.id, title: String(filter.title ?? "").slice(0, 255), selected_values: dateSelection(filter) };
    }
    if (!Array.isArray(filter.selectedValues)) throw new Error("This filter cannot be saved.");
    const selected = clone(filter.selectedValues);
    const available = selected.length ? await Promise.resolve().then(() => filter.getFilterValues()).catch(() => []) : [];
    return {
      id: filter.id,
      title: String(filter.title ?? "").slice(0, 255),
      selected_values: selected.map(value => {
        const match = available.find(candidate => matches(candidate.value, value));
        return { value, label: labelFor(match, value).slice(0, 500) };
      })
    };
  }));
  return { version: FILTER_STATE_VERSION, filters };
}

function savedFilters(state, count, hidden) {
  if (state?.version !== FILTER_STATE_VERSION || !Array.isArray(state?.filters)) throw new Error("Saved filters no longer match.");
  const saved = state.filters.filter(entry => !hidden.has(entry?.id));
  if (saved.length !== count || new Set(saved.map(f => f.id)).size !== count) throw new Error("Saved filters no longer match.");
  return saved;
}

const hiddenUpdate = (filter, defaults) => {
  const entry = (defaults?.filters ?? []).find(candidate => candidate.id === filter.id);
  if (!entry) return null;
  const previous = isDateFilter(filter) ? dateValue(filter) : filter.selectedValues;
  const values = clone(entry.selected_values.map(selected => selected.value));
  return { filter, values, date: values[0] ?? null, missing: [], previous };
};

async function resolveSelection(filter, entry) {
  if (!entry || !Array.isArray(entry.selected_values)) throw new Error("Saved filters no longer match.");
  if (isDateFilter(filter)) {
    const values = entry.selected_values.map(selected => selected?.value);
    if (values.length > 1 || !values.every(validDateValue)) throw new Error("Saved filters no longer match.");
    return { filter, date: values[0] ?? null, missing: [], title: entry.title || filter.title, previous: dateValue(filter) };
  }
  const available = entry.selected_values.length ? await filter.getFilterValues() : [];
  const missing = [];
  const values = entry.selected_values.map(selected => {
    const match = available.find(candidate => matches(candidate.value, selected?.value));
    if (!match) missing.push(selected?.label ?? fallbackLabel(selected?.value));
    return isRecord(selected?.value) ? match?.value : selected?.value;
  });
  return { filter, values, missing, title: entry.title || filter.title, previous: filter.selectedValues };
}

export async function applyFilterState(dashboard, state, { isCurrent = () => true, hiddenIds = [], defaults = null } = {}) {
  const hidden = hiddenSet(hiddenIds);
  const filters = Array.from(dashboard.filters);
  const saved = savedFilters(state, filters.filter(filter => !hidden.has(filter.id)).length, hidden);
  // Resolve every value through the current viewer's SDK before changing anything.
  // Missing/stale values fail; silently dropping one would broaden the selection.
  const updates = (await Promise.all(filters.map(filter => (hidden.has(filter.id)
    ? hiddenUpdate(filter, defaults)
    : resolveSelection(filter, saved.find(f => f.id === filter.id)))))).filter(Boolean);
  const stale = updates.find(update => update.missing.length);
  if (stale) throw new StaleFilterError(stale.title, stale.missing);
  if (!isCurrent()) throw new Error("Dashboard context changed.");
  const assign = (filter, values, date) => {
    if (isDateFilter(filter)) setDate(filter, date);
    else filter.selectedValues = values;
  };
  try {
    for (const { filter, values, date } of updates) assign(filter, values, date);
  } catch (error) {
    for (const { filter, previous } of updates) assign(filter, previous, previous);
    throw error;
  }
}

export function snapshotSelections(dashboard) {
  return Array.from(dashboard.filters).map(filter => ({ filter, dashboard, values: clone(currentValues(filter)) }));
}

const restore = (filter, values) => {
  if (isDateFilter(filter)) setDate(filter, values[0] ?? null);
  else filter.selectedValues = clone(values);
};

export function restoreSelections(snapshot) {
  const live = snapshot.map(entry => liveFilter(entry.dashboard, entry.filter));
  const previous = live.map(filter => clone(currentValues(filter)));
  try {
    snapshot.forEach(({ values }, i) => restore(live[i], values));
  } catch (error) {
    live.forEach((filter, i) => restore(filter, previous[i]));
    throw error;
  }
}

const selectionKey = values => values.map(value => key(fieldValue(value))).sort();

export function selectionsDiffer(dashboard, state, hiddenIds = []) {
  const hidden = hiddenSet(hiddenIds);
  const saved = new Map((state?.filters ?? []).map(entry => [entry.id, entry.selected_values.map(v => v.value)]));
  return Array.from(dashboard.filters).some(filter => !hidden.has(filter.id) &&
    key(selectionKey(currentValues(filter))) !== key(selectionKey(saved.get(filter.id) ?? [])));
}

export function describeFilterState(state, hiddenIds = []) {
  const hidden = hiddenSet(hiddenIds);
  const parts = (state?.filters ?? []).filter(entry => !hidden.has(entry.id) && entry.selected_values?.length)
    .map(entry => `${entry.title || "Filter"}: ${entry.selected_values.map(v => v.label).join(", ")}`);
  return parts.length ? parts.join(" · ") : "All values";
}

const savedFilterChange = (entry, live) => {
  const name = entry.title || "A";
  if (!live.has(entry.id)) return `${name} filter removed`;
  if (entry.title && live.get(entry.id) !== entry.title) return `${name} filter renamed`;
  return null;
};

export function filterStateChanges(state, dashboard, hiddenIds = []) {
  const hidden = hiddenSet(hiddenIds);
  const live = new Map(Array.from(dashboard.filters).filter(filter => !hidden.has(filter.id)).map(filter => [filter.id, filter.title]));
  const saved = (state?.filters ?? []).filter(entry => !hidden.has(entry.id));
  const savedIds = new Set(saved.map(entry => entry.id));
  const changes = saved.map(entry => savedFilterChange(entry, live)).filter(Boolean);
  for (const [id, title] of live) if (!savedIds.has(id)) changes.push(`${title || "A"} filter added`);
  return changes;
}

export function snapshotAsState(snapshot) {
  return { version: FILTER_STATE_VERSION,
    filters: snapshot.map(({ filter, values }) => ({ id: filter.id, selected_values: values.map(value => ({ value })) })) };
}
